import React, { useState } from 'react';
import { transactionsApi } from '../utils/api';
import { formatDateForInput, formatRupee } from '../utils/format';

export default function EditTransactionModal({ transaction, onClose, onSuccess }) {
  const [form, setForm] = useState({
    quantity: transaction.quantity,
    price: transaction.price,
    purchaseDate: formatDateForInput(transaction.purchaseDate),
    type: transaction.type || 'BUY',
    notes: transaction.notes || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totalValue = form.quantity && form.price
    ? parseFloat(form.quantity) * parseFloat(form.price)
    : 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await transactionsApi.update(transaction._id, {
        ...form,
        quantity: parseFloat(form.quantity),
        price: parseFloat(form.price)
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Edit Transaction</h3>
          <button className="btn btn-outline btn-icon" onClick={onClose}>✕</button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Type */}
          <div className="form-group">
            <label>Type</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {['BUY', 'SELL'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, type: t }))}
                  style={{
                    flex: 1, padding: '10px',
                    borderRadius: '8px', border: '2px solid',
                    borderColor: form.type === t ? (t === 'BUY' ? 'var(--green)' : 'var(--red)') : 'var(--border)',
                    background: form.type === t ? (t === 'BUY' ? 'var(--green-soft)' : 'var(--red-soft)') : 'transparent',
                    color: form.type === t ? (t === 'BUY' ? 'var(--green)' : 'var(--red)') : 'var(--text-muted)',
                    fontFamily: 'var(--font)', fontSize: '0.88rem', fontWeight: 700,
                    cursor: 'pointer', transition: 'var(--transition)'
                  }}
                >
                  {t === 'BUY' ? '↑ BUY' : '↓ SELL'}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row form-row-2 form-group">
            <div>
              <label>Quantity</label>
              <input
                type="number" name="quantity"
                value={form.quantity} onChange={handleChange}
                min="0.001" step="any" required
              />
            </div>
            <div>
              <label>Price per Share (₹)</label>
              <div className="input-prefix">
                <span>₹</span>
                <input
                  type="number" name="price"
                  value={form.price} onChange={handleChange}
                  min="0.01" step="any" required
                />
              </div>
            </div>
          </div>

          {totalValue > 0 && (
            <div style={{
              background: 'var(--accent-soft)', border: '1px solid var(--accent)',
              borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Total Value</span>
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)' }}>
                {formatRupee(totalValue)}
              </span>
            </div>
          )}

          <div className="form-group">
            <label>Date</label>
            <input
              type="date" name="purchaseDate"
              value={form.purchaseDate} onChange={handleChange}
              max={new Date().toISOString().split('T')[0]} required
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <input
              type="text" name="notes"
              value={form.notes} onChange={handleChange}
              placeholder="optional…" maxLength={300}
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
